import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardBody, CardHeader } from 'reactstrap';
import {
  fetchVacationBalance,
  fetchVacationExtraOrUsedBalance,
} from '../../../store/apis/leaveService';
import { ModalWrapper, ModalTable } from '../../Common';
import { VACATION_TYPES } from './constants';
import './VacationBalanceWidget.scss';
import * as icons from '../../../assets';

function VacationBalanceWidget() {
  const isLaptop = window.innerWidth <= 1366;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: 'Modal Title',
    dataConfig: null,
    data: [],
  });

  const { data: vacationBalance } = useQuery({
    queryKey: ['vacationBalance'],
    queryFn: fetchVacationBalance,
    keepPreviousData: true,
  });

  const generateDataConfig = (data = [], numericKeys = []) => {
    if (!Array.isArray(data) || data.length === 0) return [];

    const keys = Object.keys(data[0]);
    return keys.map(key => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
      render: numericKeys.includes(key)
        ? row => parseFloat(row[key]).toFixed(1)
        : undefined,
    }));
  };

  const { mutate: vacationExtraOrUsedBalanceMutation } = useMutation({
    mutationFn: vacationType =>
      fetchVacationExtraOrUsedBalance({ type: vacationType }),
    onSuccess: (dataResponse, params) => {
      if (params === VACATION_TYPES.VB_EXTRA) {
        setModalConfig({
          title: 'Details of Extra Vacation days',
          dataConfig: generateDataConfig(dataResponse, ['Days']),
          data: dataResponse,
        });
        setIsModalOpen(true);
      }
      if (params === VACATION_TYPES.VB_USED) {
        setModalConfig({
          title: 'Details of Extra Vacation days',
          dataConfig: generateDataConfig(dataResponse, ['Days']),
          data: dataResponse,
        });
        setIsModalOpen(true);
      }
    },
  });

  return (
    <Card className="vacation-widget">
      <CardHeader
        className={`vacation-widget__header pb-1 ${isLaptop ? 'pb-2' : ''}`}
      >
        <div className="d-flex flex-row align-items-center gap-2 vacation-widget__header-row">
          <img
            src={icons.iconsDashboardVacation}
            alt="Vacation Icon"
            height="20px"
          />
          <span className="fw-semibold fs-5 vacation-label">
            Vacation Balance
          </span>
        </div>
      </CardHeader>

      <CardBody className="vacation-widget__body px-3 py-1 overflow-hidden">
        <div className="vacation-widget__content">
          <div
            className="d-flex flex-column text-center gap-1 vacation-widget__item"
            title="Number of annual leave days assigned to you for the current calendar year."
          >
            <span className="vacation-label">Entitled</span>
            <span className="vacation-widget__item-value fs-4">
              {vacationBalance?.Entitled
                ? parseFloat(vacationBalance?.Entitled).toFixed(1)
                : ''}
            </span>
          </div>

          <div className="vacation-widget__item-separator" />

          <div
            className="d-flex flex-column text-center gap-1 vacation-widget__item"
            title="Additional leave days granted beyond your standard entitlement. Click the number for more details."
          >
            <span className="vacation-label">Extra</span>
            <span
              className="vacation-widget__item-value vacation-widget__item-value--link fs-4"
              onClick={() =>
                vacationExtraOrUsedBalanceMutation(VACATION_TYPES.VB_EXTRA)
              }
            >
              {vacationBalance?.Extra
                ? parseFloat(vacationBalance?.Extra).toFixed(1)
                : ''}
            </span>
          </div>

          <div className="vacation-widget__item-separator" />

          <div
            className="d-flex flex-column text-center gap-1 vacation-widget__item"
            title="Number of leave days you have already utilized within the current calendar year. Click to view the utilized leaves."
          >
            <span className="vacation-label">Used</span>
            <span
              className="vacation-widget__item-value vacation-widget__item-value--link fs-4"
              onClick={() =>
                vacationExtraOrUsedBalanceMutation(VACATION_TYPES.VB_USED)
              }
            >
              {vacationBalance?.Used
                ? parseFloat(vacationBalance?.Used).toFixed(1)
                : ''}
            </span>
          </div>

          <div className="vacation-widget__item-separator" />

          <div
            className="d-flex flex-column text-center gap-1 vacation-widget__item"
            title="Remaining leave days available for use for the current calendar year."
          >
            <span className="vacation-label">Balance</span>
            <span className="vacation-widget__item-value fs-4">
              {vacationBalance?.Balance
                ? parseFloat(vacationBalance?.Balance).toFixed(1)
                : ''}
            </span>
          </div>
        </div>
      </CardBody>

      <ModalWrapper
        isModalOpen={isModalOpen}
        toggle={() => setIsModalOpen(!isModalOpen)}
        title={modalConfig.title}
        onButtonClick={() => setIsModalOpen(false)}
        buttonText="Close"
        size="xl"
      >
        <ModalTable
          data={modalConfig.data}
          dataConfig={modalConfig.dataConfig}
        />
      </ModalWrapper>
    </Card>
  );
}

export default VacationBalanceWidget;
